import { type FC, useState, useEffect } from "react";

import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/shared/components/ui/select";
import { useIntercom } from "@/shared/providers/IntercomProvider";

interface RevokeAuthorizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRevoke: (reason: string) => Promise<void>;
  isRevoking: boolean;
  signatureType: "tpa" | "irs";
}

const REVOKE_REASONS = [
  "I no longer need Elayne's help",
  "I want to handle things myself",
  "Privacy concerns",
  "Working with a different service",
  "Other",
] as const;

const SIGNATURE_LABELS: Record<"tpa" | "irs", string> = {
  tpa: "Third-Party Authorization Agreement",
  irs: "IRS Authorization (Form 8821 / Form 56)",
};

export const RevokeAuthorizationDialog: FC<RevokeAuthorizationDialogProps> = ({
  open,
  onOpenChange,
  onRevoke,
  isRevoking,
  signatureType,
}) => {
  const { openSidePanel } = useIntercom();
  const [reason, setReason] = useState<string>("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const handleOpenChange = (value: boolean) => {
    if (!value) setReason("");
    onOpenChange(value);
  };

  const label = SIGNATURE_LABELS[signatureType];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Revoke Elayne's authorization?</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm revoking your authorization
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-secondary">
                This ends the {label} you signed. Here's what changes:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-secondary">
                <li>
                  Elayne stops contacting agencies, banks, and institutions for you. Any outreach
                  already in motion ends right away
                </li>
                <li>
                  Confirmations, statements, and account details those contacts would have brought
                  back stop arriving
                </li>
                <li>Reaching each institution becomes yours to do, one at a time</li>
                <li>What's already in your documents and estate record stays yours</li>
              </ul>
              <p className="text-sm text-secondary">
                You can re-sign anytime to re-authorize Elayne.
              </p>
            </div>

            <div className="border-t border-primary pt-4 space-y-3">
              <p className="text-sm font-medium">
                Mind telling us why? <span className="text-tertiary font-normal">(optional)</span>
              </p>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {REVOKE_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-secondary">
                Prefer to talk it through first?{" "}
                <Button
                  variant="link"
                  className="text-brand font-medium p-0 h-auto"
                  onClick={openSidePanel}
                >
                  Talk to a real person
                </Button>
              </p>
            </div>
          </div>
        </DialogBody>
        <div className="flex justify-end gap-3 pb-6">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isRevoking}>
            Keep authorization
          </Button>
          <Button variant="destructive" onClick={() => onRevoke(reason)} disabled={isRevoking}>
            {isRevoking ? "Revoking..." : "Revoke authorization"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
